/**
 * Model Hub Service - Handles API interactions for AI models
 */

import axios from 'axios';

import { post, put } from "../utils/apiUtils";


// No need to use BASE_URL as it's already configured in apiUtils.js
const API_URL = `/api/v1/persistence/ai_models`;

/**
 * Get all AI models with optional filtering
 * @param {Object} filters - Filter parameters
 * @param {Object} sort - Sort parameters
 * @returns {Promise<Array>} Promise resolving to array of models
 */
export const getModels = async (filter = {}, sort={}, query = null) => {
  try {
    const response = await post(`${API_URL}/fetch?query=${query}`, { filter: filter,sort: sort });
    return response;
  } catch (error) {
    console.error('Error fetching models:', error);
    throw error;
  }
};



/**
 * Create a new AI model
 * @param {Object} modelData - Model data
 * @returns {Promise<Object>} Promise resolving to created model
 */
export const createModel = async (modelData) => {
  try {
    const response = await post(`${API_URL}`, modelData);
    return response.data;
  } catch (error) {
    console.error('Error creating model:', error);
    throw error;
  }
};

/**
 * Update an existing AI model
 * @param {string} modelId - Model ID
 * @param {Object} modelData - Updated model data
 * @returns {Promise<Object>} Promise resolving to updated model
 */
export const updateModel = async (modelData) => {
  try {
    const response = await put(`${API_URL}`, modelData);
    return response.data;
  } catch (error) {
    console.error(`Error updating model :`, error);
    throw error;
  }
};

/**
 * Delete an AI model
 * @param {string} modelId - Model ID
 * @returns {Promise<void>} Promise resolving when model is deleted
 */
export const deleteModel = async (modelId) => {
  try {
    await axios.delete(`${API_URL}/${modelId}`);
  } catch (error) {
    console.error(`Error deleting model ${modelId}:`, error);
    throw error;
  }
};

/**
 * Deploy an AI model
 * @param {string} modelId - Model ID
 * @param {Object} deploymentConfig - Deployment configuration
 * @returns {Promise<Object>} Promise resolving to deployment status
 */
export const deployModel = async (modelId, deploymentConfig = {}) => {
  try {
    const response = await axios.post(`${API_URL}/${modelId}/deploy`, deploymentConfig);
    return response.data;
  } catch (error) {
    console.error(`Error deploying model ${modelId}:`, error);
    throw error;
  }
};

/**
 * Test an AI model with sample data
 * @param {string} modelId - Model ID
 * @param {Object} testData - Test data
 * @returns {Promise<Object>} Promise resolving to test results
 */
export const testModel = async (modelId, testData) => {
  try {
    const response = await axios.post(`${API_URL}/${modelId}/test`, testData);
    return response.data;
  } catch (error) {
    console.error(`Error testing model ${modelId}:`, error);
    throw error;
  }
};

/**
 * Get model performance metrics
 * @param {string} modelId - Model ID
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Object>} Promise resolving to performance metrics
 */
export const getModelPerformance = async (modelId, filters = {}) => {
  try {
    const response = await axios.get(`${API_URL}/${modelId}/performance`, { params: filters });
    return response.data;
  } catch (error) {
    console.error(`Error fetching performance for model ${modelId}:`, error);
    throw error;
  }
};

/**
 * Get model usage statistics
 * @param {string} modelId - Model ID
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Object>} Promise resolving to usage statistics
 */
export const getModelUsage = async (modelId, filters = {}) => {
  try {
    const response = await axios.get(`${API_URL}/${modelId}/usage`, { params: filters });
    return response.data;
  } catch (error) {
    console.error(`Error fetching usage for model ${modelId}:`, error);
    throw error;
  }
};

/**
 * Update model configuration
 * @param {string} modelId - Model ID
 * @param {Object} config - Updated configuration
 * @returns {Promise<Object>} Promise resolving to updated model
 */
export const updateModelConfig = async (modelId, config) => {
  try {
    const response = await axios.put(`${API_URL}/${modelId}/config`, config);
    return response.data;
  } catch (error) {
    console.error(`Error updating configuration for model ${modelId}:`, error);
    throw error;
  }
};

/**
 * Get model versions
 * @param {string} modelId - Model ID
 * @returns {Promise<Array>} Promise resolving to array of model versions
 */
export const getModelVersions = async (modelId) => {
  try {
    const response = await axios.get(`${API_URL}/${modelId}/versions`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching versions for model ${modelId}:`, error);
    throw error;
  }
};

/**
 * Create a new version of an existing model
 * @param {string} modelId - Model ID
 * @param {Object} versionData - Version data
 * @returns {Promise<Object>} Promise resolving to created version
 */
export const createModelVersion = async (modelId, versionData) => {
  try {
    const response = await axios.post(`${API_URL}/${modelId}/versions`, versionData);
    return response.data;
  } catch (error) {
    console.error(`Error creating version for model ${modelId}:`, error);
    throw error;
  }
};

const modelHubService = {
  getModels,
  createModel,
  updateModel,
  deleteModel,
  deployModel,
  testModel,
  getModelPerformance,
  getModelUsage,
  updateModelConfig,
  getModelVersions,
  createModelVersion,
};

export default modelHubService;

